import React from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/services/api';
import TournamentCard from '@/components/TournamentCard';
import { MainSection } from '@/components/layout/MainSection';
import { SectionContent } from '@/components/layout/SectionContent';
import { useLeagueDetailContext } from '@/layouts/LeagueDetailLayout/context';
import type { LeagueTournamentsResponse } from '@/types/league';
import styles from '../LeagueDetailPage.module.scss';

/**
 * `/matches/:leagueId/tournaments` — the conversion path of the league page:
 * the user's own tournaments in this league first (return intent), then the
 * public ones they can join (browse intent), and a single create CTA when the
 * league has nothing yet.
 */
const TournamentsTab: React.FC = () => {
  const { league, leagueId } = useLeagueDetailContext();
  const { t } = useTranslation(['league', 'tournament']);

  const { data, isLoading, error } = useQuery<LeagueTournamentsResponse>({
    queryKey: ['tournaments-by-league', leagueId],
    queryFn: async () => (await apiClient.getLeagueTournaments(leagueId)).data.data,
    enabled: !!leagueId,
  });

  const mine = data?.mine ?? [];
  const publicTournaments = data?.public ?? [];
  const isEmpty = mine.length === 0 && publicTournaments.length === 0;

  let body: React.ReactNode;
  if (error) {
    body = <p className={styles.error}>{t('league:errorLoadingTournaments')}</p>;
  } else if (isLoading) {
    body = null;
  } else if (isEmpty) {
    body = (
      <SectionContent>
        <div className={styles.emptyState}>
          <p className={styles.emptyStateText}>{t('league:noTournamentsInLeague')}</p>
          <Link className="ls-btn" to={`/tournaments/create?leagueId=${leagueId}`}>
            {t('league:createTournamentInLeague')}
          </Link>
        </div>
      </SectionContent>
    );
  } else {
    body = (
      <SectionContent>
        {mine.length > 0 && (
          <section className={styles.tournamentsGroup}>
            <h3 className={styles.groupTitle}>{t('league:myTournamentsInLeague')}</h3>
            <div className={styles.tournamentsList}>
              {mine.map((tournament) => (
                <TournamentCard key={tournament.id} tournament={tournament} showUserStats />
              ))}
            </div>
          </section>
        )}

        {publicTournaments.length > 0 && (
          <section className={styles.tournamentsGroup}>
            <h3 className={styles.groupTitle}>{t('league:publicTournamentsInLeague')}</h3>
            <div className={styles.tournamentsList}>
              {publicTournaments.map((tournament) => (
                <TournamentCard key={tournament.id} tournament={tournament} />
              ))}
            </div>
          </section>
        )}
      </SectionContent>
    );
  }

  return (
    <MainSection
      subtitleHideOnMobile
      title={league.name}
      subtitle={t('league:tournaments')}
      logoUrl={league.logoUrl || undefined}
      logoAlt={league.name}
    >
      {body}
    </MainSection>
  );
};

export default TournamentsTab;
